<template>
  <div id="tooltip" ref="tooltip" :style="tooltipPosition" :class="alignment">
    <div v-if="type === TYPE.SEAT || type === TYPE.TABLESEAT" class="content">
      <span v-if="grouped && grouped !== ''">
        <strong>Grouped Seats</strong>
        <br />
      </span>
      <span v-if="row !== '' && column !== ''">
        <strong>Row: {{ row }}, Seat: {{ column }}</strong>
        <br />
      </span>
      <span v-if="categoryName !== ''">
        Category: {{ categoryName }}
        <br />
      </span>
      <span v-if="categoryPrice !== ''">
        Price: {{ categoryPrice }}
        <br />
      </span>
      <span v-if="$app.props.channel === 'pos' && posDisplay && tpos && tpos !== ''">
        POS: {{ tpos }}
        <br />
      </span>
      <span v-else-if="$app.props.channel === 'web' && tpos && tpos !== ''">
        Pickup: {{ tpos }}
        <br />
      </span>
      <span v-if="orderName !== ''">
        Customer: {{ orderName }}
        <br />
      </span>
      <span v-if="orderEmail !== ''">
        Email: {{ orderEmail }}
        <br />
      </span>
      <span v-if="orderPhone !== ''">
        Phone: {{ orderPhone }}
        <br />
      </span>
      <span v-if="orderId !== ''">
        Order Id: {{ orderId }}, Price: {{ orderPrice }}
        <br />
      </span>
      <span v-if="status">
        <strong>{{ status }}</strong>
        <br />
      </span>
    </div>
    <div v-else-if="type === TYPE.SECTION" class="content">
      <img v-if="thumbnail" :src="thumbnail" width="200" class="rounded" />

      <span v-if="column !== ''">
        {{ column }}
        <br />
      </span>
      <span v-if="status">
        <strong>{{ status }}</strong>
        <br />
      </span>
    </div>
    <div v-else class="content">
      <span v-if="column !== ''">
        {{ column }}
        <br />
      </span>
      <span v-if="price !== ''">
        Price: {{ price }}
        <br />
      </span>
    </div>
    <div class="tail"></div>
  </div>
</template>

<script lang="ts">
import eventbus from 'tiny-emitter/instance';
import { TYPE, SEAT, SEATSTATUS, SECTIONSTATUS } from '@/constants';
import { mapState } from 'vuex';

export default {
  data() {
    return {
      type: '',
      row: '',
      column: '',
      status: '',
      price: '',
      tooltipPosition: '',
      categoryName: '',
      categoryPrice: '',
      grouped: '',
      tpos: '',
      tposId: '',
      TYPE: '',
      windowHeight: 0,
      orderId: 0,
      orderName: '',
      orderEmail: '',
      orderPhone: '',
      alignment: 'top',
      thumbnail: '',
    };
  },
  computed: {
    ...mapState('pos', ['pos']),
    ...mapState('categories', ['categories']),
    posDisplay() {
      return (
        this.tposId &&
        this.$app.props.posID &&
        this.$app.props.posID.toString().trim() !== this.tposId.toString().trim()
      );
    },
  },
  created() {
    eventbus.on('tooltip:show', this.tooltip, this);
    eventbus.on('tooltip:hide', this.tooltipHide, this);
    this.setupWindowResizeEvents();
    this.onWindowResize();
  },
  beforeDestroy() {
    eventbus.off('tooltip:show', this.tooltip, this);
    eventbus.off('tooltip:hide', this.tooltipHide, this);
    this.destroyWindowResizeEvents();
  },
  methods: {
    setupWindowResizeEvents() {
      window.addEventListener('resize', this.onWindowResize, this);
    },
    destroyWindowResizeEvents() {
      window.removeEventListener('resize', this.onWindowResize);
    },
    onWindowResize() {
      this.windowHeight = parseFloat(window.innerHeight, 10);
    },
    calculateTooltipPosition(posX, posY) {
      const position = { left: posX, top: null, bottom: null, alignment: 'top' };
      const bottomPos = this.windowHeight - posY + 2;
      //const tooltipH = parseFloat(this.$refs.tooltip.clientHeight, 10) + 60;
      const tooltipH = 150;
      if (posY <= tooltipH) {
        position.alignment = 'bottom';
        position.top = 53 + posY;
        position.left = 30 + posX;
      } else {
        position.bottom = bottomPos;
      }
      return position;
    },
    reposition(seat, e) {
      let pos = null;
      if (TYPE.SECTION === seat.type) {
        pos = this.calculateTooltipPosition(e.clientX - 30, e.clientY - 10);
      } else {
        const boundingRect = seat.view.node.getBoundingClientRect();
        pos = this.calculateTooltipPosition(boundingRect.x - 30, boundingRect.y);
      }
      const dynamicpos = pos.bottom ? `bottom: ${pos.bottom}px; top: unset;` : `top: ${pos.top}px; bottom: unset;`;
      this.tooltipPosition = `opacity:1; left: ${pos.left}px; ${dynamicpos}`;
      this.alignment = pos.alignment;
    },
    tooltip(seat, e) {
      if (this.$app.zoomManager.zoomInProgress || this.$app.zoomManager.wasPanning) return;

      if (!seat) return;

      if (seat.type === TYPE.SECTION) {
        this.type = seat.type;
        this.TYPE = TYPE;
        this.thumbnail = seat.attributes.thumbnail;
        this.column = seat.attributes.label.text;
        switch (seat.status) {
          case SECTIONSTATUS.AVAILABLE:
            this.status = '';
            break;
          case SECTIONSTATUS.UNAVAILABLE:
            this.status = 'Unavailable';
            break;
          case SECTIONSTATUS.SOLD:
            this.status = 'Sold Out';
            break;
        }
        this.reposition(seat, e);
        return;
      }

      if (seat.attributes.status == SEATSTATUS.DISABLED) {
        return;
      }
      let seatStatus = '';
      if (seat.attributes.status === SEATSTATUS.SELECTED) {
        seatStatus = 'Selected';
      } else if (seat.inCart) {
        seatStatus = 'In Cart';
      } else if (seat.attributes.status !== SEATSTATUS.FREE) {
        seatStatus = 'Unavailable';
      }
      if (this.$app.props.channel == 'pos') {
        switch (seat.attributes.status) {
          case SEATSTATUS.RESERVED:
            seatStatus = 'Reserved';
            break;
          case SEATSTATUS.LOCKED:
            seatStatus = 'Blocked';
            break;
        }
      }

      if (seat.attributes.obstructed === true) {
        if (seatStatus) {
          seatStatus += ' (Restricted View)';
        } else {
          seatStatus = 'Restricted View';
        }
      }

      this.status = seatStatus;
      this.type = seat.type;
      this.TYPE = TYPE;
      this.SEATSTATUS = SEATSTATUS;
      //this.$app.refreshCategoryList(false);
      //this.$app.refreshPosList(false);
      const catList = this.categories;
      const posList = this.pos;
      let catName = '';
      let catPrice = '';
      let posName = '';
      let posid = '';
      catList.forEach((cat) => {
        if (seat.categoryId && cat.id === seat.categoryId) {
          catName = cat.name;
          catPrice = cat.price ? cat.price : 'N/A';
        }
      });
      posList.forEach((tpos) => {
        if (seat.attributes.posId && tpos.id.toString().trim() === seat.attributes.posId.toString().trim()) {
          posName = tpos.name;
          posid = tpos.id;
        }
      });
      this.categoryName = catName;
      this.categoryPrice = catPrice;
      this.tpos = posName;
      this.tposId = posid;
      if (seat.type === TYPE.TABLESEAT) {
        this.column = seat.attributes.label !== undefined ? seat.attributes.label.trim() : '';
        this.row = seat.table.attributes.label;
        this.grouped = seat.attributes._groupId !== undefined ? seat.attributes._groupId : '';
      } else if (seat.type === TYPE.SEAT) {
        this.column = seat.attributes.label !== undefined ? seat.attributes.label.trim() : '';
        this.row = seat.row.attributes.label;
        this.grouped = seat.attributes._groupId !== undefined ? seat.attributes._groupId : '';
      } else {
        this.column = seat.attributes.label.text;
        this.categoryPrice = '';
      }
      this.row = this.row === undefined || this.row === SEAT.LABEL ? '' : this.row.trim();
      //Add order info
      let thisSeatOrder = null;
      if (
        (seat.attributes.status === SEATSTATUS.OCCUPIED || seat.attributes.status === SEATSTATUS.RESERVED) &&
        this.$app.seatmap.orders
      ) {
        this.$app.seatmap.orders.forEach((o) => {
          o.seats.forEach((s) => {
            if (s.toString().trim() === seat.uuid.toString().trim()) {
              thisSeatOrder = o;
            }
          });
        });
      }
      if (thisSeatOrder) {
        const fName = thisSeatOrder.user.firstName ? thisSeatOrder.user.firstName : '';
        const lName = thisSeatOrder.user.lastName ? thisSeatOrder.user.lastName : '';
        this.orderName = `${fName} ${lName}`;
        this.orderPhone = thisSeatOrder.user.phone ? thisSeatOrder.user.phone : '';
        this.orderEmail = thisSeatOrder.user.email ? thisSeatOrder.user.email : '';
        this.orderId = thisSeatOrder.orderId;
        this.orderPrice = thisSeatOrder.price;
        const thisSeatmapUnits = this.$app.seatmap.seatsAndTableSeats;
        thisSeatOrder.seats.forEach((uuid) => {
          const seat = thisSeatmapUnits.find((u) => u.uuid.toString().trim() === uuid.toString().trim());
          seat.shape.addClass('same-order-seats');
        });
      } else {
        this.orderName = '';
        this.orderPhone = '';
        this.orderEmail = '';
        this.orderId = '';
        this.orderPrice = '';
      }
      this.reposition(seat, e);
    },
    tooltipHide() {
      this.$app.seatmap.seatsAndTableSeats.forEach((u) => {
        if (u.shape && u.shape.hasClass('same-order-seats')) {
          u.shape.removeClass('same-order-seats');
        }
      });
      this.tooltipPosition = '';
      this.content = '';
    },
    getTransformToElement(pelem, celem) {
      return pelem.getScreenCTM().inverse().multiply(celem.getScreenCTM());
    },
  },
};
</script>

<style lang="scss">
#tooltip {
  -webkit-box-sizing: content-box;
  -moz-box-sizing: content-box;
  box-sizing: content-box;
  position: absolute;
  min-width: 100px;
  color: #fff;
  left: -9999999999px;
  top: -9999999999px;
  bottom: -9999999999px;
  height: auto;
  opacity: 0;
  -webkit-transition: opacity 0.2s ease-in-out;
  -moz-transition: opacity 0.2s ease-in-out;
  -o-transition: opacity 0.2s ease-in-out;
  -ms-transition: opacity 0.2s ease-in-out;
  transition: opacity 0.2s ease-in-out;
  &.top {
    div.content {
      padding: 10px;
      background-color: #000000; /*rgba(35,30,30,.9)*/
      -webkit-border-radius: 5px;
      -moz-border-radius: 5px;
      border-radius: 5px;
      text-align: center;
    }

    div.tail {
      position: relative;
      left: 60px;
      margin-left: -15px;
      height: 30px;
      width: 30px;
      background: url(assets/svg/tip-tail-alt.svg) left top no-repeat;
    }
  }

  &.bottom {
    /*display: flex !important;*/
    div.content {
      padding: 10px;
      background-color: #000000;
      -webkit-border-radius: 5px;
      -moz-border-radius: 5px;
      border-radius: 5px;
      text-align: center;
    }

    div.tail {
      position: absolute;
      left: 30px;
      margin-left: -15px;
      height: 30px;
      top: 1px;
      transform-origin: top left;
      width: 30px;
      transform: rotateZ(-90deg);
      background: url(assets/svg/tip-tail-alt.svg) left top no-repeat;
    }
  }
}
</style>
